import { getSupabaseClient } from "@/services/supabaseClient"
import Constants from "expo-constants"
import { Platform } from "react-native"

export const FEEDBACK_REPORT_CATEGORIES = ["bug", "usability", "feature_request", "other"] as const

export type FeedbackReportCategory = (typeof FEEDBACK_REPORT_CATEGORIES)[number]

export type SubmitFeedbackReportInput = {
  category: FeedbackReportCategory
  message: string
  contactContext?: string
  routePath?: string | null
  metadata?: Record<string, unknown>
}

export type FeedbackReport = {
  id: string
  category: FeedbackReportCategory
  message: string
  status: string
  createdAt: string
}

const MAX_MESSAGE_LENGTH = 4000
const MAX_CONTACT_LENGTH = 240

function getUserAgent(): string | null {
  if (Platform.OS !== "web" || typeof navigator === "undefined") return null
  return navigator.userAgent || null
}

function getRoutePath(inputRoutePath?: string | null): string | null {
  if (inputRoutePath) return inputRoutePath
  if (Platform.OS !== "web" || typeof window === "undefined") return null
  return `${window.location.pathname}${window.location.search}`
}

function requireValidReport(input: SubmitFeedbackReportInput) {
  if (!FEEDBACK_REPORT_CATEGORIES.includes(input.category)) {
    throw new Error("Choose a feedback category.")
  }

  const message = input.message.trim()
  if (!message) throw new Error("Describe what happened before sending the report.")
  if (message.length > MAX_MESSAGE_LENGTH) {
    throw new Error(`Keep the report under ${MAX_MESSAGE_LENGTH} characters.`)
  }

  const contactContext = input.contactContext?.trim() || null
  if (contactContext && contactContext.length > MAX_CONTACT_LENGTH) {
    throw new Error(`Keep contact context under ${MAX_CONTACT_LENGTH} characters.`)
  }

  return { message, contactContext }
}

export async function submitFeedbackReport(input: SubmitFeedbackReportInput) {
  const client = getSupabaseClient()
  if (!client) throw new Error("Supabase is not configured for this build.")

  const { data: userResult, error: userError } = await client.auth.getUser()
  if (userError) throw userError
  if (!userResult.user) throw new Error("Sign in before sending feedback.")

  const { message, contactContext } = requireValidReport(input)
  const { data, error } = await client
    .from("feedback_reports")
    .insert({
      user_id: userResult.user.id,
      category: input.category,
      message,
      contact_context: contactContext,
      platform: Platform.OS,
      app_version: Constants.expoConfig?.version ?? null,
      app_build: null,
      route_path: getRoutePath(input.routePath),
      user_agent: getUserAgent(),
      metadata: input.metadata ?? {},
    })
    .select("id,category,message,status,created_at")
    .single()

  if (error) throw error

  return {
    id: String(data.id),
    category: data.category as FeedbackReportCategory,
    message: String(data.message),
    status: String(data.status),
    createdAt: String(data.created_at),
  } satisfies FeedbackReport
}
